import React, { useState } from 'react';
import { CheckSquare, Square } from 'lucide-react';

/**
 * Selector granular de slides
 * Permite al usuario elegir qué slides incluir/excluir
 */
const SlideSelector = ({ slides, onSlidesChange, isOpen, onClose }) => {
  const [selectedSlides, setSelectedSlides] = useState(
    slides.reduce((acc, slide, idx) => {
      acc[idx] = true;
      return acc;
    }, {})
  );

  const handleToggleSlide = (idx) => {
    const updated = { ...selectedSlides, [idx]: !selectedSlides[idx] };
    setSelectedSlides(updated);
  };

  const handleToggleAll = () => {
    const allSelected = Object.values(selectedSlides).every(v => v);
    const updated = Object.keys(selectedSlides).reduce((acc, key) => {
      acc[key] = !allSelected;
      return acc;
    }, {});
    setSelectedSlides(updated);
  };

  const handleApply = () => {
    const selectedIndices = Object.keys(selectedSlides)
      .filter(idx => selectedSlides[idx])
      .map(idx => parseInt(idx));

    const filteredSlides = slides.filter((_, idx) => selectedIndices.includes(idx));
    onSlidesChange(filteredSlides);
    onClose();
  };

  const selectedCount = Object.values(selectedSlides).filter(v => v).length;
  const allSelected = selectedCount === slides.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 modal-safe-area bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Seleccionar Slides</h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectedCount} de {slides.length} slides seleccionados
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Acciones rápidas */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={handleToggleAll}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              allSelected
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {allSelected ? (
              <>
                <CheckSquare size={18} />
                Deseleccionar Todo
              </>
            ) : (
              <>
                <Square size={18} />
                Seleccionar Todo
              </>
            )}
          </button>
        </div>

        {/* Lista de slides */}
        <div className="flex-1 overflow-y-auto space-y-2 mb-4">
          {slides.map((slide, idx) => (
            <div
              key={slide.id || idx}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                selectedSlides[idx]
                  ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
              }`}
              onClick={() => handleToggleSlide(idx)}
            >
              <div className="flex items-center gap-3">
                {selectedSlides[idx] ? (
                  <CheckSquare className="w-5 h-5 text-blue-500 flex-shrink-0" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">
                    Slide {idx + 1}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {slide.type === 'cover' && '📌 Portada'}
                    {slide.type === 'storytelling' && '📖 Storytelling'}
                    {slide.type === 'storytelling_results' && '📊 Resultados Finales'}
                    {slide.type === 'campaigns' && '🎯 Top Campañas'}
                    {slide.type === 'closing' && '👋 Cierre'}
                    {!['cover', 'storytelling', 'storytelling_results', 'campaigns', 'closing'].includes(slide.type) && slide.type}
                  </p>

                  {/* Vista previa del contenido */}
                  <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                    {slide.editable_elements?.[0]?.content ||
                      slide.editable_elements?.[0]?.title ||
                      'Sin contenido'}
                  </div>
                </div>

                {/* Indicadores */}
                <div className="flex items-center gap-1">
                  {slide.table && (
                    <span title="Contiene tabla" className="text-lg">
                      📊
                    </span>
                  )}
                  {slide.chart && (
                    <span title="Contiene gráfico" className="text-lg">
                      📈
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer - Botones de acción */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleApply}
            disabled={selectedCount === 0}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
          >
            Aplicar ({selectedCount} slides)
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlideSelector;
